import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { combineNodeOffsetMaps, cryptoLikeId, graphLayoutOffsets, inferGraphTimeZone, type AutoConnection } from '../../../core/entities/graph-document';
import type { ConnectionStrategy } from '../../../core/ports/preferences-repository';
import {
  createHumanNode,
  createOccupationNode,
  createProjectNode,
  type JourneyNode,
  type NodeType
} from '../../../core/entities/node';
import { estimateArchivePreflight } from '../../../core/policies/archive/validate-archive';
import { canCreateNodeInAccessMode, type AccessMode } from '../../../core/policies/access-gate/preview-node-entitlement';
import { createVisualThemeProfile } from '../../../core/policies/workspace/visual-theme-profile';
import { resolveSceneInteractionMode, type SceneInteractionMode } from '../../../core/policies/workspace/scene-interaction-mode';
import { IndexedDbMediaRepository } from '../../../storage/implementations/indexeddb-media-repository';
import { visibleNodes } from '../../../core/policies/search/focus-visualization';
import { GraphCanvasLayer, type PositionedNode } from '../renderers/canvas-layers/GraphCanvasLayer';
import { GraphScene3D } from '../renderers/scene-3d/GraphScene3D';
import { cosmicBackgroundStyle } from '../renderers/vector-fallbacks/cosmic-fallback';
import { GraphAppearancePanel } from './GraphAppearancePanel';
import { useGraphStore } from '../services/graph-store';
import {
  selectGraphFocusContext,
  selectSceneInteractionModeForWorkspace,
  selectWorkspaceGraphSurface,
} from '../services/graph-focus-context';
import { detectGraphMotionProfile } from '../services/graph-motion-profile';
import { layoutNodesForViewport, measureLayoutBounds, pickNodeAtPoint, worldPointFromCanvasPoint } from '../services/graph-interaction';
import { fitCameraToBounds, focusCameraOnNode, isCameraMoving, isDragGesture, zoomCameraAtCanvasPoint } from '../services/graph-camera';
import { hasNodeMotion } from '../services/graph-node-motion';
import { useWorkspaceModeStore } from '../services/workspace-mode-store';
import { acquireWorkspaceEditLock, releaseWorkspaceEditLock, type WorkspaceEditLockMap } from '../../../core/policies/workspace/edit-lock';
import { VisibilityFilterBar } from './VisibilityFilterBar';
import { FocusedNodeOverlay } from './FocusedNodeOverlay';
import { DeleteNodeConfirmDialog } from './DeleteNodeConfirmDialog';
import { WorkspaceModeSwitcher } from './WorkspaceModeSwitcher';
import { SearchOverlay } from '../../search/components/SearchOverlay';
import { canonicalRelationshipPairId } from '../../../core/policies/explicit-relationships';
import {
  selectRelationshipSummary,
  type VisibleRelationshipEdge,
} from '../../../core/policies/explicit-relationships';
import { composeGraphDisplayEdges, type GraphDisplayConnection } from '../../../core/policies/graph-display';
import { ArchiveToolbar } from '../../archive-transfer/components/ArchiveToolbar';
import { ArchiveErrorDialog } from '../../archive-transfer/components/ArchiveErrorDialog';
import { ImportArchiveConfirmDialog } from '../../archive-transfer/components/ImportArchiveConfirmDialog';
import { ExportArchiveDialog, type ExportArchiveOptions } from '../../archive-transfer/components/ExportArchiveDialog';
import { importPreparedArchiveIntoGraphStore, prepareGraphArchiveImport, type PreparedArchiveGraph } from '../../archive-transfer/services/archive-graph-loader';
import { ArchiveTransferService } from '../../archive-transfer/services/archive-transfer-service';
import { archiveFileNameFromTitle } from '../../archive-transfer/services/archive-file-name';
import { summarizeArchiveSpatialStatus } from '../../archive-transfer/services/archive-spatial-status';
import { canPickArchiveSaveLocation, isArchiveSaveCancelled, saveArchiveBlob } from '../../archive-transfer/services/browser-archive-save';
import { NodeEditorPanel } from '../../node-editor/components/NodeEditorPanel';
import { RelationshipDetailSheet, RelationshipPanel } from '../../relationships/components';
import { ConnectionStrategySwitcher } from '../../preferences/components/ConnectionStrategySwitcher';
import { ConnectionDisplayModeSwitcher } from '../../preferences/components/ConnectionDisplayModeSwitcher';
import { useConnectionDisplayMode } from '../../preferences/connection-display/ConnectionDisplayModeProvider';
import { useConnectionStrategy } from '../../preferences/connection-strategy/ConnectionStrategyProvider';
import { LanguageSwitcher } from '../../preferences/components/LanguageSwitcher';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { authConfigFromEnv, createWorkerArchiveSigningClient, type FullModeAuthSession } from '../../access-gate/services/google-auth-unlock-service';
import { SettingsWorkspace } from '../../preferences/components/SettingsWorkspace';
import { ensureDeidentificationGraphSync, getCurrentGraphAIAssistConfig } from '../../preferences/services/deidentification-settings-store';
import { AIAssistWorkspace } from '../../ai-assist/components/AIAssistWorkspace';
import { ensureAIWorkspaceSync } from '../../ai-assist/services/ai-workspace-store';
import { OutlineWorkspace } from '../../outline-workspace/components/OutlineWorkspace';
import {
	deserializeGraphAppearanceConfig,
  graphNodeTypeVisualControls,
	serializeGraphAppearanceConfig,
  synchronizeGraphNodeTypeVisualConfig,
  updateGraphNodeTypeBrightness,
  updateGraphNodeTypeColor,
  updateGraphNodeTypeLabelsVisible,
  type GraphNodeTypeVisualConfig,
} from '../services/graph-appearance-config';
import {
	loadStoredGraphAppearanceConfig,
	saveStoredGraphAppearanceConfig,
} from '../services/graph-appearance-preferences';

export function ImmersiveGraphView({ accessMode = 'unlocked', authSession = null }: { accessMode?: AccessMode; authSession?: FullModeAuthSession | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const renderFrameRef = useRef<number | null>(null);
  const activeEditLocksRef = useRef<WorkspaceEditLockMap>({});
  const appearancePersistenceGraphIdRef = useRef<string | null>(null);
  const archiveTransferServiceRef = useRef(new ArchiveTransferService(new IndexedDbMediaRepository()));
  const sceneRef = useRef<{
    positionedNodes: PositionedNode[];
    connections: GraphDisplayConnection[];
    visibleRelationshipEdges: VisibleRelationshipEdge[];
    highlighted: GraphDisplayConnection[];
    focusedNodeId: string | null;
    camera: typeof camera;
    viewport: typeof viewport;
  }>({
    positionedNodes: [],
    connections: [],
    visibleRelationshipEdges: [],
    highlighted: [],
    focusedNodeId: null,
    camera: useGraphStore.getState().camera,
    viewport: { width: window.innerWidth, height: window.innerHeight },
  });
  const suppressClickRef = useRef(false);
  const dragStateRef = useRef<{
    dragToken: string;
    mode: 'camera' | 'node';
    nodeId: string | null;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    moved: boolean;
  } | null>(null);
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  const [editingNode, setEditingNode] = useState<JourneyNode | null>(null);
  const [relationshipEditorNodeId, setRelationshipEditorNodeId] = useState<string | null>(null);
  const [relationshipEditorTargetNodeId, setRelationshipEditorTargetNodeId] = useState<string | null>(null);
  const [relationshipDetailState, setRelationshipDetailState] = useState<{ pairId: string; sourceNodeId: string } | null>(null);
  const [archiveError, setArchiveError] = useState<{ detail: string } | null>(null);
  const [archiveAccessNoticeVisible, setArchiveAccessNoticeVisible] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState<{ action: 'import' | 'export'; title: string; warnings: string[] } | null>(null);
  const [importingArchive, setImportingArchive] = useState(false);
  const [exportingArchive, setExportingArchive] = useState(false);
  const [pendingArchiveImport, setPendingArchiveImport] = useState<PreparedArchiveGraph | null>(null);
  const [exportDialogState, setExportDialogState] = useState<{ fileName: string; title: string; warnings: string[] } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiWorkspaceOpen, setAIWorkspaceOpen] = useState(false);
  const [workspaceControlsOpen, setWorkspaceControlsOpen] = useState(false);
  const [appearancePanelOpen, setAppearancePanelOpen] = useState(false);
  const [sceneInteractionMode, setSceneInteractionMode] = useState<SceneInteractionMode>('navigate');
  const [typeVisualConfig, setTypeVisualConfig] = useState<Record<string, GraphNodeTypeVisualConfig>>(() => ({}));
  const [pendingDeleteNode, setPendingDeleteNode] = useState<JourneyNode | null>(null);
  const [activeEditLocks, setActiveEditLocks] = useState<WorkspaceEditLockMap>({});
  const { locale, setLocale, t } = useLocale();
  const authConfig = authConfigFromEnv();
  const { connectionStrategy, setConnectionStrategy } = useConnectionStrategy();
  const { connectionDisplayMode, setConnectionDisplayMode } = useConnectionDisplayMode();
  const currentMode = useWorkspaceModeStore((state) => state.currentMode);
  const currentGraphMode = useWorkspaceModeStore((state) => state.currentGraphMode);
  const setWorkspaceMode = useWorkspaceModeStore((state) => state.setMode);
  const returnToGraphMode = useWorkspaceModeStore((state) => state.returnToGraphMode);
  const {
    graph,
    camera,
    nodeMotionOffsets,
    nodeOffsets,
    visibleType,
    setVisibleType,
    focusedNodeId,
    focus,
    saveNode,
    deleteNode,
    saveRelationshipPair,
    deleteRelationshipPair,
    setGraphTitle,
    readonlyMode,
    setAccessMode,
    setConnectionStrategy: setGraphConnectionStrategy,
    panCameraBy,
    setCameraVelocity,
    setCameraTarget,
    advanceCameraInertia,
    resetCameraVelocity,
    moveNodeBy,
    commitNodeOffset,
    settleNodeMotion,
    setNodeVelocity,
    resetNodeVelocity,
    advanceNodeMotion,
  } = useGraphStore();
  const canCreateNewNode = canCreateNodeInAccessMode(accessMode, graph.nodes.length);
  const archiveLocked = accessMode !== 'unlocked';
  const nodeCreationDisabledReason = !canCreateNewNode ? t('previewNodeLimitReached') : '';
  const shouldElevateActionPanel = !editingNode && (Boolean(focusedNodeId) || settingsOpen || aiWorkspaceOpen);
  const savePickerSupported = useMemo(() => canPickArchiveSaveLocation(), []);
  const anchorOffsets = useMemo(() => graphLayoutOffsets(graph.layout), [graph.layout]);
  const anchoredNodeIds = useMemo(() => new Set(Object.keys(anchorOffsets)), [anchorOffsets]);
  const renderedOffsets = useMemo(
    () => combineNodeOffsetMaps(anchorOffsets, nodeOffsets, nodeMotionOffsets),
    [anchorOffsets, nodeMotionOffsets, nodeOffsets]
  );
  const nodes = useMemo(() => visibleNodes(graph.nodes, visibleType), [graph.nodes, visibleType]);
  const resolvedTypeVisualConfig = useMemo(
	() => synchronizeGraphNodeTypeVisualConfig(graph.nodes, typeVisualConfig, t),
	[graph.nodes, t, typeVisualConfig]
  );
  const typeVisualControls = useMemo(
	() => graphNodeTypeVisualControls(graph.nodes, resolvedTypeVisualConfig),
	[graph.nodes, resolvedTypeVisualConfig]
  );
  const anchoredPositionedNodes = useMemo<PositionedNode[]>(
    () => layoutNodesForViewport(nodes, viewport.width, viewport.height, anchorOffsets, anchoredNodeIds),
    [anchorOffsets, anchoredNodeIds, nodes, viewport.height, viewport.width]
  );
  const positionedNodes = useMemo<PositionedNode[]>(
    () => layoutNodesForViewport(nodes, viewport.width, viewport.height, renderedOffsets, anchoredNodeIds),
    [anchoredNodeIds, nodes, renderedOffsets, viewport.height, viewport.width]
  );
  const graphSurface = useMemo(
    () => selectWorkspaceGraphSurface(currentMode, currentGraphMode),
    [currentGraphMode, currentMode]
  );
  const focusContext = useMemo(
    () => selectGraphFocusContext({ activeEditLocks, focusedNodeId, graph, visibleType }),
    [activeEditLocks, focusedNodeId, graph, visibleType]
  );
  const focusedNode = focusContext.focusedNode;
  const relationshipEditorNode = graph.nodes.find((node) => node.nodeId === relationshipEditorNodeId) ?? null;
  const relationshipDetailPair = graph.relationships?.find((pair) => pair.pairId === relationshipDetailState?.pairId) ?? null;
  const relationshipDetailSourceNode = graph.nodes.find((node) => node.nodeId === relationshipDetailState?.sourceNodeId) ?? null;
  const relationshipDetailTargetNode = relationshipDetailPair && relationshipDetailSourceNode
    ? graph.nodes.find((node) => node.nodeId === relationshipDetailPair.endpoints.find((endpoint) => endpoint !== relationshipDetailSourceNode.nodeId)) ?? null
    : null;
  const relationshipConnectionGroups = useMemo(() => groupConnectionsByPairId(graph.connections), [graph.connections]);
  const graphDisplayEdges = useMemo(
    () => composeGraphDisplayEdges({
      connections: graph.connections,
      displayMode: connectionDisplayMode,
      relationships: graph.relationships ?? [],
      focusedNodeId,
      expandedRelationship: relationshipDetailState,
    }),
    [connectionDisplayMode, focusedNodeId, graph.connections, graph.relationships, relationshipDetailState],
  );
  const visibleRelationshipEdges = graphDisplayEdges.relationshipEdges;
  const focusedRelationshipSummaries = useMemo(() => {
    if (!focusedNode) {
      return [];
    }

    return (graph.relationships ?? [])
      .filter((pair) => pair.endpoints.includes(focusedNode.nodeId))
      .map((pair) => {
        const targetNodeId = pair.endpoints[0] === focusedNode.nodeId ? pair.endpoints[1] : pair.endpoints[0];
        const targetNode = graph.nodes.find((node) => node.nodeId === targetNodeId);

        return {
          pairId: pair.pairId,
          targetNodeLabel: targetNode?.displayName ?? targetNodeId,
          summary: selectRelationshipSummary({
            pair,
            sourceNodeId: focusedNode.nodeId,
            targetNodeId,
            derivedConnections: relationshipConnectionGroups.get(pair.pairId) ?? [],
            visibilityMode: 'focus-visible',
          }),
        };
      });
  }, [focusedNode, graph.nodes, graph.relationships, relationshipConnectionGroups]);
  const relationshipDetailSummary = useMemo(() => {
    if (!relationshipDetailPair || !relationshipDetailSourceNode || !relationshipDetailTargetNode) {
      return null;
    }

    return selectRelationshipSummary({
      pair: relationshipDetailPair,
      sourceNodeId: relationshipDetailSourceNode.nodeId,
      targetNodeId: relationshipDetailTargetNode.nodeId,
      derivedConnections: relationshipConnectionGroups.get(relationshipDetailPair.pairId) ?? [],
      visibilityMode: 'expand-visible',
    });
  }, [relationshipConnectionGroups, relationshipDetailPair, relationshipDetailSourceNode, relationshipDetailTargetNode]);
  const relationshipDetailDerivedReasons = useMemo(
    () => relationshipDetailPair ? (relationshipConnectionGroups.get(relationshipDetailPair.pairId) ?? []).map((connection) => connection.reasonSummary) : [],
    [relationshipConnectionGroups, relationshipDetailPair],
  );
  const focusedPositionedNode = positionedNodes.find((node) => node.nodeId === focusedNodeId) ?? null;
  const highlighted = focusContext.highlighted;
  const displayHighlighted = graphDisplayEdges.highlightedConnections;
  const motionProfile = useMemo(() => detectGraphMotionProfile(), []);
  const visualTheme = useMemo(() => createVisualThemeProfile(motionProfile), [motionProfile]);
  const show3DScene = graphSurface.show3DScene;
  const show2DCanvas = graphSurface.show2DCanvas;
  const focusedNodeLockedByOutline = focusContext.focusedNodeLockedByOutline;
  const currentWorkspaceLabel = workspaceModeLabel(currentMode, t);

  useEffect(() => {
	setSceneInteractionMode((mode) => selectSceneInteractionModeForWorkspace({
		currentGraphMode,
		currentMode,
		readonlyMode,
		requestedMode: mode,
	}));
  }, [currentGraphMode, currentMode, readonlyMode]);

  useEffect(() => {
    ensureDeidentificationGraphSync();
    ensureAIWorkspaceSync();
  }, []);

  useEffect(() => {
    setAccessMode(accessMode);
    if (accessMode === 'unlocked') {
      setArchiveAccessNoticeVisible(false);
    }
  }, [accessMode, setAccessMode]);

  useEffect(() => {
    let active = true;
    appearancePersistenceGraphIdRef.current = null;
    setTypeVisualConfig({});

    void loadStoredGraphAppearanceConfig(graph.graphId).then((storedConfig) => {
      if (!active) {
        return;
      }

      setTypeVisualConfig(
        synchronizeGraphNodeTypeVisualConfig(graph.nodes, deserializeGraphAppearanceConfig(storedConfig), t),
      );
      appearancePersistenceGraphIdRef.current = graph.graphId;
    });

    return () => {
      active = false;
    };
  }, [graph.graphId]);

  useEffect(() => {
	setTypeVisualConfig((currentConfig) => synchronizeGraphNodeTypeVisualConfig(graph.nodes, currentConfig, t));
  }, [graph.nodes, t]);

  useEffect(() => {
    if (appearancePersistenceGraphIdRef.current !== graph.graphId) {
      return;
    }

    void saveStoredGraphAppearanceConfig(graph.graphId, serializeGraphAppearanceConfig(typeVisualConfig));
  }, [graph.graphId, typeVisualConfig]);

  useEffect(() => {
    setGraphConnectionStrategy(connectionStrategy);
  }, [connectionStrategy, setGraphConnectionStrategy]);

  useEffect(() => {
    function handleResize() {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    sceneRef.current = {
      positionedNodes,
      connections: graphDisplayEdges.connections,
      visibleRelationshipEdges,
      highlighted: displayHighlighted,
      focusedNodeId,
      camera,
      viewport,
    };
  }, [camera, displayHighlighted, focusedNodeId, graphDisplayEdges.connections, positionedNodes, viewport, visibleRelationshipEdges]);

  useEffect(() => {
    activeEditLocksRef.current = activeEditLocks;
  }, [activeEditLocks]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (renderFrameRef.current !== null) {
        cancelAnimationFrame(renderFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!show2DCanvas) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const layer = new GraphCanvasLayer(canvas);

    const renderFrame = (timeMs: number) => {
      const scene = sceneRef.current;
      canvas.width = scene.viewport.width;
      canvas.height = scene.viewport.height;
      layer.render(
        scene.positionedNodes,
        scene.connections,
        scene.visibleRelationshipEdges,
        scene.highlighted,
        scene.focusedNodeId,
        scene.camera,
        timeMs,
        typeVisualConfig,
      );
      renderFrameRef.current = requestAnimationFrame(renderFrame);
    };

    renderFrameRef.current = requestAnimationFrame(renderFrame);

    return () => {
      if (renderFrameRef.current !== null) {
        cancelAnimationFrame(renderFrameRef.current);
        renderFrameRef.current = null;
      }
    };
  }, [show2DCanvas, typeVisualConfig]);

  useEffect(() => {
    if (!show2DCanvas) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (event: WheelEvent) => {
      if (dragStateRef.current) return;

      event.preventDefault();
      stopCameraAnimation();
      resetCameraVelocity();

      const liveCamera = useGraphStore.getState().camera;
      const next = zoomCameraAtCanvasPoint(
        liveCamera,
        canvasPointFromClient(canvas, event.clientX, event.clientY),
        event.deltaY
      );

      setCameraTarget(next.targetX, next.targetY, next.targetScale);
      startCameraInertia();
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [resetCameraVelocity, setCameraTarget, show2DCanvas]);

  useEffect(() => {
    if (positionedNodes.length === 0) return;

    if (focusedPositionedNode) {
      const next = focusCameraOnNode(camera, focusedPositionedNode, viewport);
      setCameraTarget(next.targetX, next.targetY, next.targetScale);
      startCameraInertia();
      return;
    }

    const bounds = measureLayoutBounds(anchoredPositionedNodes);
    const overview = fitCameraToBounds(camera, bounds, viewport);
    setCameraTarget(overview.targetX, overview.targetY, overview.targetScale);
    startCameraInertia();
  }, [graph.graphId, graph.updatedAt, focusedNodeId, setCameraTarget, viewport.height, viewport.width, visibleType]);

  function beginCreate(type: NodeType) {
    if (readonlyMode || !canCreateNewNode) {
      return;
    }

    setEditingNode(createDraftNode(type));
  }

  function tryAcquireEditLock(nodeId: string, owner: 'outline-inline' | 'node-editor-overlay'): boolean {
    const result = acquireWorkspaceEditLock(activeEditLocksRef.current, {
      nodeId,
      owner,
      acquiredAt: new Date().toISOString(),
    });

    if (!result.granted) {
      return false;
    }

    activeEditLocksRef.current = result.locks;
    setActiveEditLocks(result.locks);
    return true;
  }

  function releaseEditLock(nodeId: string, owner?: 'outline-inline' | 'node-editor-overlay') {
    const nextLocks = releaseWorkspaceEditLock(activeEditLocksRef.current, nodeId, owner);
    activeEditLocksRef.current = nextLocks;
    setActiveEditLocks(nextLocks);
  }

  function beginEditNode(node: JourneyNode) {
    if (graph.nodes.some((currentNode) => currentNode.nodeId === node.nodeId)) {
      const granted = tryAcquireEditLock(node.nodeId, 'node-editor-overlay');
      if (!granted) {
        return;
      }

	  focus(node.nodeId);
    }

    setEditingNode(node);
  }

  function handleConnectionStrategyChange(nextConnectionStrategy: ConnectionStrategy) {
    setConnectionStrategy(nextConnectionStrategy);
    setGraphConnectionStrategy(nextConnectionStrategy);
  }

  function beginEditFocusedNode() {
    if (focusedNode) {
      beginEditNode(focusedNode);
    }
  }

  function openRelationshipEditor(node: JourneyNode, initialTargetNodeId: string | null = null) {
    if (!graph.nodes.some((candidate) => candidate.nodeId === node.nodeId)) {
      return;
    }

    const selectableTargetNodeId = initialTargetNodeId
      && initialTargetNodeId !== node.nodeId
      && graph.nodes.some((candidate) => candidate.nodeId === initialTargetNodeId)
      ? initialTargetNodeId
      : null;

    setRelationshipDetailState(null);
    setRelationshipEditorNodeId(node.nodeId);
    setRelationshipEditorTargetNodeId(selectableTargetNodeId);
  }

  function handleSaveRelationship(input: Parameters<typeof saveRelationshipPair>[0]) {
    saveRelationshipPair(input);
    setRelationshipEditorNodeId(null);
    setRelationshipEditorTargetNodeId(null);
    setRelationshipDetailState({
      pairId: canonicalRelationshipPairId(input.sourceNodeId, input.targetNodeId),
      sourceNodeId: input.sourceNodeId,
    });
  }

  function handleDeleteRelationship(firstNodeId: string, secondNodeId: string) {
    deleteRelationshipPair(firstNodeId, secondNodeId);
    setRelationshipDetailState(null);
    setRelationshipEditorNodeId(null);
    setRelationshipEditorTargetNodeId(null);
  }

  function handleSave(node: JourneyNode) {
    const isNewNode = !graph.nodes.some((candidate) => candidate.nodeId === node.nodeId);
    if (isNewNode && !canCreateNewNode) {
      return;
    }

    saveNode(node);
    focus(node.nodeId);
    releaseEditLock(node.nodeId, 'node-editor-overlay');
    setEditingNode(null);
  }

  function requestDeleteFocusedNode() {
    if (focusedNode) {
      setPendingDeleteNode(focusedNode);
    }
  }

  function handleConfirmDeleteNode() {
    if (!pendingDeleteNode) {
      return;
    }

    deleteNode(pendingDeleteNode.nodeId);
    releaseEditLock(pendingDeleteNode.nodeId);
    if (editingNode?.nodeId === pendingDeleteNode.nodeId) {
      setEditingNode(null);
    }
    focus(null);
    setPendingDeleteNode(null);
  }

  function finalizeImportedGraph(
  graphDocument: typeof graph,
  warnings: string[],
  graphAppearanceConfig?: Parameters<typeof deserializeGraphAppearanceConfig>[0],
  ) {
    setVisibleType('All');
    focus(null);
    setEditingNode(null);
    activeEditLocksRef.current = {};
    setActiveEditLocks({});
    if (graphAppearanceConfig) {
      appearancePersistenceGraphIdRef.current = graphDocument.graphId;
      setTypeVisualConfig(
        synchronizeGraphNodeTypeVisualConfig(
          graphDocument.nodes,
          deserializeGraphAppearanceConfig(graphAppearanceConfig),
          t,
        ),
      );
    }
    setArchiveError(null);
    setArchiveStatus({ action: 'import', title: graphDocument.title, warnings });
  }

  async function handleImportArchive(file: File) {
	if (archiveLocked) {
		showArchiveAccessNotice();
		return;
	}

	if (readonlyMode) {
		return;
	}

    setImportingArchive(true);
    setArchiveError(null);
    setArchiveAccessNoticeVisible(false);
    try {
      const signingClient = authSession && authConfig ? createWorkerArchiveSigningClient(authSession, authConfig.workerUrl) : undefined;
      const prepared = await prepareGraphArchiveImport(await new Response(file).arrayBuffer(), {
        requireSignedLargeArchives: false,
        signatureVerifier: signingClient,
      });

      if (graph.nodes.length > 0) {
        setPendingArchiveImport(prepared);
        return;
      }

      await performPreparedArchiveImport(prepared);
    } catch (error) {
      setArchiveError({ detail: extractArchiveErrorDetail(error) });
    } finally {
      setImportingArchive(false);
    }
  }

  async function handleConfirmImportArchive() {
	if (archiveLocked) {
		showArchiveAccessNotice();
		return;
	}

	if (readonlyMode || !pendingArchiveImport) return;

    setImportingArchive(true);
    try {
      await performPreparedArchiveImport(pendingArchiveImport);
      setPendingArchiveImport(null);
    } catch (error) {
      setArchiveError({ detail: extractArchiveErrorDetail(error) });
    } finally {
      setImportingArchive(false);
    }
  }

  async function performPreparedArchiveImport(prepared: PreparedArchiveGraph) {
    const result = await importPreparedArchiveIntoGraphStore(prepared, { replaceConfirmed: true });

    if (!result.imported) {
      return;
    }

		finalizeImportedGraph(prepared.graph, result.warnings, prepared.graphAppearanceConfig);
  }

  function handleExportArchive() {
    if (archiveLocked) {
      showArchiveAccessNotice();
      return;
    }

    setArchiveError(null);
    setArchiveAccessNoticeVisible(false);
    const preflight = estimateArchivePreflight(graph);
    setExportDialogState({ fileName: archiveFileNameFromTitle(graph.title), title: graph.title, warnings: preflight.warnings });
  }

  async function handleExportArchiveSubmit(options: ExportArchiveOptions) {
    if (archiveLocked) {
      showArchiveAccessNotice();
      return;
    }

    setExportingArchive(true);
    setArchiveError(null);
    setArchiveAccessNoticeVisible(false);

    try {
      const preflight = estimateArchivePreflight(graph);
      const blob = await archiveTransferServiceRef.current.exportGraph(graph, {
        archiveTitle: options.title,
        transientNodeOffsets: combineNodeOffsetMaps(nodeOffsets, nodeMotionOffsets),
        aiAssistConfig: getCurrentGraphAIAssistConfig(),
			graphAppearanceConfig: serializeGraphAppearanceConfig(resolvedTypeVisualConfig),
        archiveSigner: authSession && authConfig ? createWorkerArchiveSigningClient(authSession, authConfig.workerUrl) : undefined,
      });

      await saveArchiveBlob(blob, { fileName: options.fileName, mode: options.saveMode });
      if (options.syncGraphTitle && options.title !== graph.title) {
        setGraphTitle(options.title);
      }
      setArchiveStatus({ action: 'export', title: options.title, warnings: preflight.warnings });
      setExportDialogState(null);
    } catch (error) {
      if (isArchiveSaveCancelled(error)) return;
      setArchiveError({ detail: extractArchiveErrorDetail(error) });
    } finally {
      setExportingArchive(false);
    }
  }

  function showArchiveAccessNotice() {
    setArchiveAccessNoticeVisible(true);
    setArchiveError(null);
    setExportDialogState(null);
    setPendingArchiveImport(null);
  }

  function stopCameraAnimation() {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }

  function startCameraInertia() {
    stopCameraAnimation();

    const tick = () => {
      advanceCameraInertia();
      advanceNodeMotion();
      const state = useGraphStore.getState();
      if (!isCameraMoving(state.camera) && !hasNodeMotion(state.nodeVelocities)) {
        animationFrameRef.current = null;
        return;
      }

      animationFrameRef.current = requestAnimationFrame(tick);
    };

    animationFrameRef.current = requestAnimationFrame(tick);
  }

  function canvasPointFromClient(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  function handleCanvasClick(event: React.MouseEvent<HTMLCanvasElement>) {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    const canvas = event.currentTarget;
    const hit = pickNodeAtPoint(positionedNodes, worldPointFromCanvasPoint(canvasPointFromClient(canvas, event.clientX, event.clientY), camera));

    focus(hit?.nodeId ?? null);
  }

  function beginDrag(canvas: HTMLCanvasElement, dragToken: string, clientX: number, clientY: number) {
    if (dragStateRef.current) return;

    stopCameraAnimation();
    resetCameraVelocity();
    suppressClickRef.current = false;
    const hit = pickNodeAtPoint(positionedNodes, worldPointFromCanvasPoint(canvasPointFromClient(canvas, clientX, clientY), camera));
    if (!hit) {
      setCameraTarget(camera.x, camera.y, camera.scale);
    } else {
      settleNodeMotion(hit.nodeId);
    }
    dragStateRef.current = {
      dragToken,
      mode: hit ? 'node' : 'camera',
      nodeId: hit?.nodeId ?? null,
      startX: clientX,
      startY: clientY,
      lastX: clientX,
      lastY: clientY,
      moved: false,
    };
  }

  function updateDrag(dragToken: string, clientX: number, clientY: number) {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.dragToken !== dragToken) return;

    const deltaX = clientX - dragState.lastX;
    const deltaY = clientY - dragState.lastY;
    if (deltaX === 0 && deltaY === 0) return;

    if (dragState.mode === 'node' && dragState.nodeId) {
      moveNodeBy(dragState.nodeId, deltaX / camera.scale, deltaY / camera.scale);
      setNodeVelocity(dragState.nodeId, deltaX / camera.scale, deltaY / camera.scale);
    } else {
      panCameraBy(deltaX, deltaY);
      setCameraVelocity(deltaX, deltaY);
    }

    const moved = dragState.moved || isDragGesture(clientX - dragState.startX, clientY - dragState.startY);
    dragStateRef.current = {
      ...dragState,
      lastX: clientX,
      lastY: clientY,
      moved,
    };
    suppressClickRef.current = moved;
  }

  function endDrag(dragToken: string) {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.dragToken !== dragToken) return;

    dragStateRef.current = null;

    if (dragState.mode === 'node') {
      resetCameraVelocity();
      if (dragState.nodeId) {
        commitNodeOffset(dragState.nodeId);
        if (dragState.moved || suppressClickRef.current) {
          startCameraInertia();
        } else {
          resetNodeVelocity(dragState.nodeId);
        }
      }
      return;
    }

    resetCameraVelocity();
  }

  function handleCanvasPointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (event.button !== 0) return;
    beginDrag(event.currentTarget, `pointer:${event.pointerId}`, event.clientX, event.clientY);
  }

  function handleCanvasPointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    updateDrag(`pointer:${event.pointerId}`, event.clientX, event.clientY);
  }

  function handleCanvasPointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    endDrag(`pointer:${event.pointerId}`);
  }

  function handleCanvasMouseDown(event: ReactMouseEvent<HTMLCanvasElement>) {
    if (event.button !== 0) return;
    beginDrag(event.currentTarget, 'mouse', event.clientX, event.clientY);
  }

  function handleCanvasMouseMove(event: ReactMouseEvent<HTMLCanvasElement>) {
    updateDrag('mouse', event.clientX, event.clientY);
  }

  function handleCanvasMouseUp() {
    endDrag('mouse');
  }

  return (
    <main
      aria-label="Fullscreen star map"
      data-motion-profile={motionProfile}
      data-theme-profile={visualTheme.profileId}
      style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden', background: cosmicBackgroundStyle() }}
    >
      {show2DCanvas ? (
        <canvas
          ref={canvasRef}
          aria-label="JourneyNodes graph canvas"
          onClick={handleCanvasClick}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onPointerDown={handleCanvasPointerDown}
          onPointerMove={handleCanvasPointerMove}
          onPointerUp={handleCanvasPointerUp}
          style={{ width: '100%', height: '100%', cursor: 'pointer' }}
        />
      ) : null}
      {show2DCanvas ? (
    <div className="graph-appearance-shell graph-appearance-shell--floating" style={{ position: 'absolute', right: 16, bottom: 16, zIndex: 7 }}>
      <div className="floating-panel">
        <GraphAppearancePanel
          controls={typeVisualControls}
          expanded={appearancePanelOpen}
          onExpandedChange={setAppearancePanelOpen}
          onToggleTypeLabels={(typeId) => setTypeVisualConfig((currentConfig) => updateGraphNodeTypeLabelsVisible(currentConfig, typeId))}
          onTypeBrightnessChange={(typeId, brightness) =>
            setTypeVisualConfig((currentConfig) => updateGraphNodeTypeBrightness(currentConfig, typeId, brightness))
          }
          onTypeColorChange={(typeId, colorHex) =>
            setTypeVisualConfig((currentConfig) => updateGraphNodeTypeColor(currentConfig, typeId, colorHex))
          }
          panelId="graph-canvas-appearance-panel"
        />
      </div>
    </div>
    ) : null}
      {show3DScene ? (
        <GraphScene3D
          appearancePanelOpen={appearancePanelOpen}
          focusedNodeId={focusedNodeId}
          displayConnections={graphDisplayEdges.connections}
          graph={graph}
          highlightedConnections={displayHighlighted}
          interactionMode={sceneInteractionMode}
          motionProfile={motionProfile}
          onAppearancePanelOpenChange={setAppearancePanelOpen}
          onFocus={focus}
          onCommitNodePlacement={(nodeId, deltaX, deltaY, deltaZ) => {
			moveNodeBy(nodeId, deltaX, deltaY, deltaZ);
			commitNodeOffset(nodeId);
		  }}
      onInteractionModeChange={(mode) => setSceneInteractionMode(selectSceneInteractionModeForWorkspace({
      currentGraphMode,
      currentMode,
      readonlyMode,
      requestedMode: mode,
      }))}
          onResetOverview={() => focus(null)}
          onTypeVisualConfigChange={setTypeVisualConfig}
          readonlyMode={readonlyMode}
          themeProfileId={visualTheme.profileId}
          typeVisualConfig={resolvedTypeVisualConfig}
          visibleRelationshipEdges={visibleRelationshipEdges}
          visibleType={visibleType}
        />
      ) : null}
      <div style={{ position: 'absolute', left: 16, top: 16 }} className="floating-panel">
        <SearchOverlay
          nodes={graph.nodes}
          onFocus={focus}
          visibleType={visibleType}
          onRevealAndFocus={(nodeId, _nodeType) => {
            setVisibleType('All');
            focus(nodeId);
          }}
        />
      </div>
      <div style={{ position: 'absolute', left: 16, bottom: 16 }} className="floating-panel">
        <VisibilityFilterBar value={visibleType} onChange={setVisibleType} />
        <ArchiveToolbar
          importing={importingArchive}
          exporting={exportingArchive}
          locked={archiveLocked}
          lockedReason={t('archiveLockedContactAuthor')}
          readonly={readonlyMode}
          onExportArchive={handleExportArchive}
          onImportArchive={handleImportArchive}
          onLockedAction={showArchiveAccessNotice}
        />
        {archiveAccessNoticeVisible && (
          <p className="hud-hint archive-toolbar__notice" role="alert">{t('archiveLockedContactAuthor')}</p>
        )}
        {archiveStatus && <p role="status" style={{ margin: '12px 0 0', maxWidth: 360 }}>{formatArchiveStatus(archiveStatus, t)}</p>}
      </div>
      {pendingArchiveImport && (
        <div style={{ position: 'absolute', left: 16, bottom: 172, maxWidth: 420 }}>
          <ImportArchiveConfirmDialog
            currentTitle={graph.title}
            importing={importingArchive}
            incomingTitle={pendingArchiveImport.graph.title}
            onCancel={() => setPendingArchiveImport(null)}
            onConfirm={handleConfirmImportArchive}
            warnings={pendingArchiveImport.warnings}
          />
        </div>
      )}
      {exportDialogState && (
        <div style={{ position: 'absolute', left: 16, bottom: pendingArchiveImport ? 424 : 172, maxWidth: 420 }}>
          <ExportArchiveDialog
            exporting={exportingArchive}
            initialFileName={exportDialogState.fileName}
            initialTitle={exportDialogState.title}
            onCancel={() => setExportDialogState(null)}
            onSubmit={handleExportArchiveSubmit}
            savePickerSupported={savePickerSupported}
            warnings={exportDialogState.warnings}
          />
        </div>
      )}
      <div style={{ position: 'absolute', left: 16, bottom: pendingArchiveImport ? (exportDialogState ? 680 : 424) : (exportDialogState ? 428 : 172), maxWidth: 420 }}>
        <ArchiveErrorDialog message={archiveError ? formatArchiveError(archiveError, t) : ''} onClose={() => setArchiveError(null)} />
      </div>
      <div className="workspace-controls-shell" style={{ position: 'absolute', right: 16, top: 16, zIndex: shouldElevateActionPanel ? 12 : 8 }}>
        <button
          aria-controls="workspace-controls-panel"
          aria-expanded={workspaceControlsOpen}
          aria-label={t('workspace.mode.controls')}
          className="floating-panel workspace-controls-toggle"
          onClick={() => setWorkspaceControlsOpen((open) => !open)}
          type="button"
        >
          <span className="mode-switcher__eyebrow">{t('workspace.mode.current')}</span>
          <strong>{currentWorkspaceLabel}</strong>
          <span className="workspace-controls-toggle__hint">
            {workspaceControlsOpen ? t('workspace.mode.controlsHide') : t('workspace.mode.controlsShow')}
          </span>
        </button>
        {workspaceControlsOpen ? (
          <div className="floating-panel workspace-controls-panel">
            <section aria-label="Node actions" className="hud-stack" id="workspace-controls-panel">
              <WorkspaceModeSwitcher currentGraphMode={currentGraphMode} currentMode={currentMode} onModeChange={setWorkspaceMode} showStatus={false} />
              <LanguageSwitcher locale={locale} onChange={setLocale} />
              <ConnectionDisplayModeSwitcher connectionDisplayMode={connectionDisplayMode} onChange={setConnectionDisplayMode} />
              <ConnectionStrategySwitcher connectionStrategy={connectionStrategy} onChange={handleConnectionStrategyChange} />
              <div role="group" aria-label="Create node" className="hud-toolbar">
                <button className="hud-chip" onClick={() => setSettingsOpen((open) => !open)} type="button">
                  {t('settings.workspace.title')}
                </button>
                <button className="hud-chip" onClick={() => setAIWorkspaceOpen((open) => !open)} type="button">
                  {t('workspace.aiAssist.title')}
                </button>
                {(['Human', 'Project', 'Occupation'] as const).map((type) => (
                  <button className="hud-chip" disabled={readonlyMode || !canCreateNewNode} key={type} onClick={() => beginCreate(type)} type="button">
                    {formatActionLabel(locale, t('create'), nodeTypeLabel(type, t))}
                  </button>
                ))}
              </div>
              {nodeCreationDisabledReason && <p className="hud-hint" role="note">{nodeCreationDisabledReason}</p>}
              {focusedNode && (
                <button className="hud-subtle-button" disabled={readonlyMode || focusedNodeLockedByOutline} onClick={beginEditFocusedNode} type="button">
                  {t('editFocusedNode')}
                </button>
              )}
            </section>
          </div>
        ) : null}
      </div>
      <div className="workspace-outline-shell">
          <OutlineWorkspace
            activeEditLocks={activeEditLocks}
            currentGraphMode={currentGraphMode}
            focusedNodeId={focusedNodeId}
            graph={graph}
            onBeginInlineEdit={(nodeId) => tryAcquireEditLock(nodeId, 'outline-inline')}
            onCancelInlineEdit={(nodeId) => releaseEditLock(nodeId, 'outline-inline')}
            onCreate={beginCreate}
            onEdit={beginEditNode}
            onLocate={(nodeId, nodeType) => {
              if (visibleType !== 'All' && visibleType !== nodeType) {
                setVisibleType('All');
              }
              focus(nodeId);
              returnToGraphMode();
            }}
            onRequestDelete={setPendingDeleteNode}
            onReturnToGraph={returnToGraphMode}
            onSaveInlineEdit={(node) => {
              saveNode(node);
              focus(node.nodeId);
              releaseEditLock(node.nodeId, 'outline-inline');
            }}
            open={currentMode === 'outline'}
            readonly={readonlyMode}
            createDisabled={!canCreateNewNode}
            createDisabledReason={nodeCreationDisabledReason}
            motionProfile={motionProfile}
            themeProfileId={visualTheme.profileId}
            visibleType={visibleType}
          />
        </div>
      {(editingNode || relationshipEditorNode || relationshipDetailSourceNode || settingsOpen || aiWorkspaceOpen) && (
        <div className="workspace-dock">
          {editingNode && (
          <NodeEditorPanel
            manageRelationshipsDisabled={!graph.nodes.some((node) => node.nodeId === editingNode.nodeId)}
            node={editingNode}
            onManageRelationships={() => openRelationshipEditor(editingNode)}
            onSave={handleSave}
            onCancel={() => {
              if (graph.nodes.some((node) => node.nodeId === editingNode.nodeId)) {
                releaseEditLock(editingNode.nodeId, 'node-editor-overlay');
              }
              setEditingNode(null);
            }}
            readonly={readonlyMode}
            saveDisabledReason={!graph.nodes.some((node) => node.nodeId === editingNode.nodeId) ? nodeCreationDisabledReason : ''}
            saveLabel={graph.nodes.some((node) => node.nodeId === editingNode.nodeId) ? t('saveNode') : t('createNode')}
          />
          )}
          {relationshipEditorNode && (
            <RelationshipPanel
              initialTargetNodeId={relationshipEditorTargetNodeId}
              nodes={graph.nodes}
              onClose={() => {
                setRelationshipEditorNodeId(null);
                setRelationshipEditorTargetNodeId(null);
              }}
              onDelete={handleDeleteRelationship}
              onSave={handleSaveRelationship}
              readonly={readonlyMode}
              relationships={graph.relationships ?? []}
              sourceNode={relationshipEditorNode}
            />
          )}
          {relationshipDetailSourceNode && (
            <RelationshipDetailSheet
              onClose={() => setRelationshipDetailState(null)}
              onEdit={() => {
                setRelationshipDetailState(null);
                openRelationshipEditor(relationshipDetailSourceNode, relationshipDetailTargetNode?.nodeId ?? null);
              }}
              derivedReasonSummaries={relationshipDetailDerivedReasons}
              pair={relationshipDetailPair}
              readonly={readonlyMode}
              sourceNode={relationshipDetailSourceNode}
              summary={relationshipDetailSummary}
              targetNode={relationshipDetailTargetNode}
            />
          )}
          <SettingsWorkspace onClose={() => setSettingsOpen(false)} open={settingsOpen} />
          <AIAssistWorkspace onClose={() => setAIWorkspaceOpen(false)} open={aiWorkspaceOpen} />
        </div>
      )}
      {pendingDeleteNode && (
        <div className="workspace-editor-backdrop">
          <DeleteNodeConfirmDialog
            nodeName={pendingDeleteNode.displayName}
            onCancel={() => setPendingDeleteNode(null)}
            onConfirm={handleConfirmDeleteNode}
          />
        </div>
      )}
      <FocusedNodeOverlay
        node={focusedNode}
        connections={highlighted}
        originTimeZone={inferGraphTimeZone(graph)}
        onClearFocus={() => focus(null)}
        onEditNode={beginEditFocusedNode}
        onManageRelationships={() => focusedNode && openRelationshipEditor(focusedNode)}
        onViewRelationshipDetails={(pairId) => focusedNode && setRelationshipDetailState({ pairId, sourceNodeId: focusedNode.nodeId })}
        onDeleteNode={requestDeleteFocusedNode}
        relationships={focusedRelationshipSummaries}
        editDisabled={readonlyMode || focusedNodeLockedByOutline}
        deleteDisabled={readonlyMode || focusedNodeLockedByOutline}
        relationshipReadonly={readonlyMode}
      />
    </main>
  );
}

function workspaceModeLabel(mode: '2d' | '3d' | 'outline', t: ReturnType<typeof useLocale>['t']): string {
  if (mode === '3d') {
    return t('workspace.mode.3dLabel');
  }

  if (mode === 'outline') {
    return t('workspace.mode.outlineLabel');
  }

  return t('workspace.mode.2dLabel');
}

function extractArchiveErrorDetail(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown archive error.';
}

function createDraftNode(type: NodeType): JourneyNode {
  const nodeId = `node-${cryptoLikeId()}`;

  switch (type) {
    case 'Human':
      return createHumanNode({ nodeId, displayName: '' });
    case 'Project':
      return createProjectNode({ nodeId, displayName: '' });
    case 'Occupation':
      return createOccupationNode({ nodeId, displayName: '' });
  }
}

function groupConnectionsByPairId(connections: AutoConnection[]): Map<string, AutoConnection[]> {
  const groups = new Map<string, AutoConnection[]>();

  for (const connection of connections) {
    const pairId = canonicalRelationshipPairId(connection.sourceNodeId, connection.targetNodeId);
    groups.set(pairId, [...(groups.get(pairId) ?? []), connection]);
  }

  return groups;
}

function formatArchiveStatus(status: { action: 'import' | 'export'; title: string; warnings: string[] }, t: ReturnType<typeof useLocale>['t']): string {
	const spatialStatus = summarizeArchiveSpatialStatus(status.warnings);
	const layoutStatus = spatialStatus.hasSpatialFallback
		? t('archiveStatusSpatialFallbackApplied')
		: t('archiveStatusSpatialRoundTripReady');

  if (status.action === 'export') {
    if (status.warnings.length > 0) {
      return `${formatMessage(t('archiveExportedWarnings'), { title: status.title, warnings: status.warnings.join('; ') })} ${layoutStatus}`;
    }

    return `${formatMessage(t('archiveExported'), { title: status.title })} ${layoutStatus}`;
  }

  if (status.warnings.length > 0) {
    return `${formatMessage(t('archiveImportedWarnings'), { title: status.title, warnings: status.warnings.join('; ') })} ${layoutStatus}`;
  }

  return `${formatMessage(t('archiveImported'), { title: status.title })} ${layoutStatus}`;
}

function formatArchiveError(error: { detail: string }, t: ReturnType<typeof useLocale>['t']): string {
  return formatMessage(t('archiveImportFailed'), { detail: error.detail });
}

function nodeTypeLabel(type: NodeType, t: ReturnType<typeof useLocale>['t']): string {
  if (type === 'Human') return t('human');
  if (type === 'Project') return t('project');
  return t('occupation');
}

function formatActionLabel(locale: 'zh-TW' | 'en', action: string, target: string): string {
  return locale === 'zh-TW' ? `${action}${target}` : `${action} ${target}`;
}

function formatMessage(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}
